import React, { useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { ChevronDown, Search } from 'lucide-react'
import { sortSelectOptions, type SelectOption } from '../lib/sortSelectOptions'

type SearchableSelectProps = {
  value: string
  onChange: (value: string) => void
  options: SelectOption[]
  placeholder?: string
  /** When true (default), options are sorted alphabetically by label */
  sortAlphabetically?: boolean
  className?: string
  disabled?: boolean
  'aria-label'?: string
  id?: string
}

type PanelPos = {
  top: number
  left: number
  width: number
}

export function SearchableSelect({
  value,
  onChange,
  options,
  placeholder = 'Select...',
  sortAlphabetically = true,
  className = '',
  disabled = false,
  'aria-label': ariaLabel,
  id,
}: SearchableSelectProps) {
  const autoId = useId()
  const controlId = id ?? autoId
  const rootRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const searchRef = useRef<HTMLInputElement>(null)
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [panelPos, setPanelPos] = useState<PanelPos | null>(null)

  const sortedOptions = useMemo(
    () => (sortAlphabetically ? sortSelectOptions(options) : options),
    [options, sortAlphabetically],
  )

  const selected = sortedOptions.find((o) => o.value === value)

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return sortedOptions
    return sortedOptions.filter((o) => {
      const hay = `${o.label} ${o.keywords ?? ''} ${o.value}`.toLowerCase()
      return hay.includes(q)
    })
  }, [sortedOptions, query])

  const updatePanelPosition = () => {
    const trigger = triggerRef.current
    if (!trigger) return
    const rect = trigger.getBoundingClientRect()
    const width = Math.min(Math.max(rect.width, 240), 360)
    const maxLeft = window.innerWidth - width - 8
    const left = Math.min(Math.max(8, rect.left), maxLeft)
    
    // Check if dropdown panel would overflow bottom of viewport
    const expectedHeight = 220
    const fitsBelow = rect.bottom + expectedHeight <= window.innerHeight
    const top = fitsBelow ? rect.bottom + 6 : Math.max(8, rect.top - expectedHeight - 6)
    setPanelPos({ top, left, width })
  }

  useLayoutEffect(() => {
    if (!open) {
      setPanelPos(null)
      return
    }
    updatePanelPosition()
    const onReflow = () => updatePanelPosition()
    window.addEventListener('resize', onReflow)
    window.addEventListener('scroll', onReflow, true)
    return () => {
      window.removeEventListener('resize', onReflow)
      window.removeEventListener('scroll', onReflow, true)
    }
  }, [open])

  useEffect(() => {
    if (!open) return
    const onDocClick = (e: MouseEvent) => {
      const target = e.target as Node
      if (rootRef.current?.contains(target) || panelRef.current?.contains(target)) return
      setOpen(false)
      setQuery('')
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false)
        setQuery('')
      }
    }
    document.addEventListener('mousedown', onDocClick)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDocClick)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  useEffect(() => {
    if (open) {
      requestAnimationFrame(() => searchRef.current?.focus())
    }
  }, [open, panelPos])

  const pick = (next: string) => {
    onChange(next)
    setOpen(false)
    setQuery('')
  }

  const panel = open && panelPos
    ? createPortal(
        <div
          ref={panelRef}
          style={{ top: panelPos.top, left: panelPos.left, width: panelPos.width }}
          className="fixed z-[200] rounded-xl border border-[var(--border-gold)] bg-[var(--bg-surface)] shadow-[0_12px_40px_rgba(0,0,0,0.35)] overflow-hidden"
        >
          <div className="p-2 border-b border-[var(--border)] bg-[var(--bg-main)]">
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 w-3.5 h-3.5 text-[var(--gold-main)] pointer-events-none" />
              <input
                ref={searchRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search..."
                className="w-full bg-[var(--bg-surface)] border border-[var(--border)] rounded-lg py-2 pl-8 pr-2 text-xs text-[var(--text-primary)] outline-none focus:border-[var(--gold-main)]"
              />
            </div>
          </div>

          <ul role="listbox" aria-labelledby={controlId} className="max-h-52 overflow-y-auto py-1">
            {filtered.length === 0 ? (
              <li className="px-3 py-2.5 text-xs text-[var(--text-faint)]">No matches found</li>
            ) : (
              filtered.map((opt) => (
                <li key={opt.value || '__empty__'} role="option" aria-selected={opt.value === value}>
                  <button
                    type="button"
                    onClick={() => pick(opt.value)}
                    className={`w-full text-left px-3 py-2 text-xs leading-snug break-words transition ${
                      opt.value === value
                        ? 'bg-[var(--gold-soft-bg)] text-[var(--gold-main)] font-semibold'
                        : 'text-[var(--text-primary)] hover:bg-[var(--bg-surface-2)]'
                    }`}
                  >
                    {opt.label}
                  </button>
                </li>
              ))
            )}
          </ul>
        </div>,
        document.body,
      )
    : null

  return (
    <div ref={rootRef} className={className}>
      <button
        ref={triggerRef}
        type="button"
        id={controlId}
        disabled={disabled}
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => !disabled && setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-2 bg-[var(--bg-main)] border border-[var(--border)] focus:border-[var(--gold-main)] rounded-xl py-2.5 px-3 text-xs text-[var(--text-primary)] outline-none disabled:opacity-50 disabled:cursor-not-allowed text-left"
      >
        <span className={`truncate ${selected ? '' : 'text-[var(--text-faint)]'}`}>
          {selected?.label ?? placeholder}
        </span>
        <ChevronDown className={`w-4 h-4 shrink-0 text-[var(--text-muted)] transition ${open ? 'rotate-180' : ''}`} />
      </button>
      {panel}
    </div>
  )
}
